import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import dayjs, { type Dayjs } from 'dayjs';
import { onError } from '../../utils/errors';
import TextField, { TextFieldProps } from './TextField';

export function DateField({
  errorText,
  inputProps,
  minDate,
  maxDate,
  name,
  onBlur,
  onChange,
  sx,
  value,
  ...rest
}: DateFieldProps) {
  function onDateChange(date: Dayjs | null) {
    onChange({ name: name || '', value: dayJsToDate(date) as Date });
  }

  function onDateBlur(date: Dayjs | null) {
    onBlur?.({ name: name || '', value: dayJsToDate(date) as Date });
  }

  // TODO: DateFieldProps should extends DatePickerProps<Dayjs, Dayjs>
  return (
    <DatePicker
      inputFormat="YYYY-MM-DD"
      mask="____-__-__"
      maxDate={dateToDayJs(maxDate) as any}
      minDate={dateToDayJs(minDate) as any}
      onChange={onDateChange as any}
      renderInput={(params) => {
        return (
          <TextField
            errorText={errorText}
            onBlur={onDateBlur as any}
            sx={{ mb: 0, width: 120, ...sx }}
            {...inputProps}
            {...params}
          />
        );
      }}
      value={dateToDayJs(value) || null}
      {...rest}
    />
  );
}

function dateToDayJs(date?: Date): Dayjs | undefined {
  return date ? dayjs(date).utc() : undefined;
}

function dayJsToDate(date?: Dayjs | null): Date | undefined {
  try {
    return date?.toDate();
  } catch (e) {
    onError(
      e,
      [
        'Date error. Please send this message to Pulse admin.',
        `Locale: ${getLocale()}.`,
        `Date: ${JSON.stringify(date)}.`,
      ].join(' '),
    );
    console.error("Can't convert dateJs date", date, e);
    return new Date();
  }
}

function getLocale() {
  try {
    return Intl.NumberFormat().resolvedOptions().locale;
  } catch {
    // Intl isn't available
  }

  if (window.navigator.languages) {
    return JSON.stringify(window.navigator.languages);
  }

  return (window.navigator as any).userLanguage || window.navigator.language;
}

export interface DateFieldProps extends Omit<
  DatePickerProps<Dayjs, Date>,
  'date' | 'onChange' | 'openPicker' | 'rawValue' | 'renderInput' | 'value'
> {
  errorText?: string;
  inputProps?: TextFieldProps;
  name?: string;
  onBlur?: (event: { name: string; value: Date }) => void;
  onChange: (event: { name: string; value: Date }) => void;
  sx?: TextFieldProps['sx'];
  value?: Date;
}
